import { InsforgeUser } from '@insforge/shared';
import { useInsforge } from '../provider/InsforgeProvider';

export interface useUserReturn {
  user: InsforgeUser | null | undefined;
  isLoaded: boolean;
  updateUser: (data: InsforgeUser['profile']) => Promise<{ error: string } | null>;
  setUser: (user: InsforgeUser | null) => void;
}

/**
 * Hook to access current user data
 *
 * @returns Object containing:
 *   - `user`: Current user object (InsforgeUser | null)
 *   - `isLoaded`: Boolean indicating if auth state has been loaded
 *   - `updateUser`: Function to update user profile data (returns { error: string } | null)
 *   - `setUser`: Internal function to manually set user state
 *
 * @example
 * ```tsx
 * function UserProfile() {
 *   const { user, isLoaded, updateUser } = useUser();
 *
 *   if (!isLoaded) return <div>Loading...</div>;
 *   if (!user) return <div>Not signed in</div>;
 *
 *   async function handleUpdate(name: string) {
 *     const result = await updateUser({ name });
 *     if (result?.error) {
 *       console.error(result.error);
 *     } else {
 *       console.log('User updated successfully');
 *     }
 *   }
 *
 *   return (
 *     <div>
 *       <p>Email: {user.email}</p>
 *       {user.name && <p>Name: {user.name}</p>}
 *       {user.avatarUrl && <img src={user.avatarUrl} alt="Avatar" />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useUser(): useUserReturn {
  const { user, isLoaded, updateUser, setUser } = useInsforge();
  return { user, isLoaded, updateUser, setUser };
}
